import React from "react";
import { container } from "@/utils/mobx";
import { Clickable } from "@/components/toolkit/Clickable";
import { ActivityBarStore } from "./store";
import { SSHKeysSettings } from "./SSHKeysSettings";
import { t } from "@/i18n";
import { useAction } from "@/components/toolkit/InputProvider";

export const SettingsModal = container((props: { store: ActivityBarStore }) => {
    const store = props.store;

    useAction('settings-modal', 'close', () => store.cancelSettings());

    // Pin the modal's initial (centered) top so switching tabs — which changes
    // content height — grows the modal downward instead of re-centering it.
    const dialogRef = React.useRef<HTMLDivElement>(null);
    const [topOffset, setTopOffset] = React.useState<number | null>(null);

    React.useLayoutEffect(() => {
        if (!store.isSettingsModalOpen) {
            setTopOffset(null);
            return;
        }
        if (topOffset === null && dialogRef.current) {
            setTopOffset(dialogRef.current.getBoundingClientRect().top);
        }
    }, [store.isSettingsModalOpen, topOffset]);

    if (!store.isSettingsModalOpen) return null;

    return (
        <div
            className="activity_bar__dialog_overlay"
            data-input-component="settings-modal"
            data-input-focus="move"
            onMouseDown={() => store.cancelSettings()}
            style={topOffset !== null ? { alignItems: "flex-start" } : undefined}
        >
            <div
                ref={dialogRef}
                className="activity_bar__dialog activity_bar__settings_modal"
                onMouseDown={e => e.stopPropagation()}
                style={topOffset !== null ? { marginTop: topOffset, maxHeight: `calc(100dvh - ${topOffset}px - 20px)` } : undefined}
            >
                <div className="activity_bar__dialog_title">{t("settings_title")}</div>
                <div className="activity_bar__settings_tabs">
                    {([
                        { id: "appearance", label: t("settings_tab_appearance") },
                        { id: "claude",     label: t("settings_tab_claude") },
                        { id: "tabs",       label: t("settings_tab_tabs") },
                        { id: "ssh-keys",   label: t("settings_tab_ssh_keys") },
                    ] as const).map(({ id, label }) => (
                        <Clickable key={id} onClick={() => store.setSettingsTab(id)}>
                            <button className={"activity_bar__settings_tab" + (store.settingsTab === id ? " active" : "")}>
                                {label}
                            </button>
                        </Clickable>
                    ))}
                </div>
                <div className="activity_bar__dialog_body">
                {store.settingsTab === "appearance" && <>
                <div className="activity_bar__settings_section">
                    <div className="activity_bar__settings_section_title">{t("settings_theme")}</div>
                    <div className="activity_bar__theme_list">
                        {store.themes.map(theme => (
                            <Clickable key={theme.id} onClick={() => store.previewTheme(theme.id)}>
                                <button className={"activity_bar__theme_item" + (store.currentTheme === theme.id ? " active" : "")}>
                                    <span
                                        className="activity_bar__theme_swatch"
                                        style={{ backgroundColor: theme.bg }}
                                    >
                                        <span
                                            className="activity_bar__theme_swatch_accent"
                                            style={{ backgroundColor: theme.accent }}
                                        />
                                    </span>
                                    <span className="activity_bar__theme_name">{theme.name}</span>
                                    {store.currentTheme === theme.id && (
                                        <span className="activity_bar__theme_check">✓</span>
                                    )}
                                </button>
                            </Clickable>
                        ))}
                    </div>
                </div>
                <div className="activity_bar__settings_section">
                    <div className="activity_bar__settings_section_title">{t("settings_language")}</div>
                    <div className="activity_bar__language_list">
                        {store.languages.map(lang => (
                            <Clickable key={lang.id} onClick={() => store.previewLanguage(lang.id)}>
                                <button className={"activity_bar__language_item" + (store.currentLanguage === lang.id ? " active" : "")}>
                                    <span className="activity_bar__language_name">{lang.nativeName}</span>
                                    <span className="activity_bar__language_subname">{lang.name}</span>
                                    {store.currentLanguage === lang.id && (
                                        <span className="activity_bar__theme_check">✓</span>
                                    )}
                                </button>
                            </Clickable>
                        ))}
                    </div>
                </div>
                </>}
                {store.settingsTab === "claude" && <>
                <div className="activity_bar__settings_section">
                    <div className="activity_bar__settings_section_title">{t("settings_claude_model")}</div>
                    <div className="activity_bar__language_list">
                        {([
                            { id: "",                   label: t("settings_claude_model_default") },
                            { id: "claude-sonnet-4-6", label: "Sonnet 4.6" },
                            { id: "sonnet",            label: "Sonnet" },
                            { id: "claude-opus-5-5",   label: "Opus 5.5" },
                            { id: "opus",              label: "Opus" },
                            { id: "haiku",             label: "Haiku" },
                        ] as const).map(({ id, label }) => (
                            <Clickable key={id} onClick={() => store.setClaudeModel(id)}>
                                <button className={"activity_bar__language_item" + (store.currentClaudeModel === id ? " active" : "")}>
                                    <span className="activity_bar__language_name">{label}</span>
                                    {store.currentClaudeModel === id && (
                                        <span className="activity_bar__theme_check">✓</span>
                                    )}
                                </button>
                            </Clickable>
                        ))}
                    </div>
                </div>
                <div className="activity_bar__settings_section">
                    <div className="activity_bar__settings_section_title">{t("settings_claude_effort")}</div>
                    <div className="activity_bar__language_list">
                        {([
                            { id: "",       label: t("settings_claude_effort_default") },
                            { id: "low",    label: t("settings_claude_effort_low") },
                            { id: "medium", label: t("settings_claude_effort_medium") },
                            { id: "high",   label: t("settings_claude_effort_high") },
                            { id: "max",    label: t("settings_claude_effort_max") },
                        ] as const).map(({ id, label }) => (
                            <Clickable key={id} onClick={() => store.setClaudeEffort(id)}>
                                <button className={"activity_bar__language_item" + (store.currentClaudeEffort === id ? " active" : "")}>
                                    <span className="activity_bar__language_name">{label}</span>
                                    {store.currentClaudeEffort === id && (
                                        <span className="activity_bar__theme_check">✓</span>
                                    )}
                                </button>
                            </Clickable>
                        ))}
                    </div>
                </div>
                </>}
                {store.settingsTab === "tabs" && <>
                <div className="activity_bar__settings_section">
                    <div className="activity_bar__settings_section_title">{t("settings_max_open_tabs_desktop")}</div>
                    <input
                        type="number"
                        min={1}
                        max={50}
                        className="activity_bar__settings_number_input"
                        value={store.currentMaxOpenTabsDesktop}
                        onChange={e => store.previewMaxOpenTabsDesktop(Math.max(1, parseInt(e.target.value) || 1))}
                    />
                </div>
                <div className="activity_bar__settings_section">
                    <div className="activity_bar__settings_section_title">{t("settings_max_open_tabs_mobile")}</div>
                    <input
                        type="number"
                        min={1}
                        max={50}
                        className="activity_bar__settings_number_input"
                        value={store.currentMaxOpenTabsMobile}
                        onChange={e => store.previewMaxOpenTabsMobile(Math.max(1, parseInt(e.target.value) || 1))}
                    />
                </div>
                </>}
                {store.settingsTab === "ssh-keys" && <SSHKeysSettings />}
                </div>
                <div className="activity_bar__dialog_actions">
                    <Clickable onClick={() => store.cancelSettings()}>
                        <button className="activity_bar__dialog_btn activity_bar__dialog_btn--cancel">{t("settings_cancel")}</button>
                    </Clickable>
                    <Clickable onClick={() => store.saveSettings()}>
                        <button className="activity_bar__dialog_btn activity_bar__dialog_btn--create">{t("settings_save")}</button>
                    </Clickable>
                </div>
            </div>
        </div>
    );
}, ActivityBarStore);
